import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BoxView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxView';
import * as classnames from 'classnames/bind';
import {css} from 'glamor';
import {untracked} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./SurfacePage.less');
const cx = classnames.bind(styles);

export interface ISurfacePageProps
{
    page:Page;
    isActive:boolean;
    isOverlay:boolean;
    allowAnimation:boolean;
    isHidden?:boolean;
    stackIndex?:number;
    isReactive?:boolean;
}

interface ISurfacePageState
{
    animationClassNames:string[];
}

@observer
export class SurfacePage extends React.Component<ISurfacePageProps, ISurfacePageState>
{
    public static defaultProps:Partial<ISurfacePageProps> = {
        stackIndex: -1,
        isHidden: false,
        isReactive: true
    };

    state:ISurfacePageState = {
        animationClassNames: []
    };

    private _focusSurfaceArea():void
    {
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;
        const isInPreviewMode:boolean = controllers.ui.surfaceArea.isInPreviewOrZenPreviewMode();

        !isInPreviewMode && requestAnimationFrame(() => surfaceAreaDomElement && surfaceAreaDomElement.focus());
    }

    componentDidUpdate(prevProps:ISurfacePageProps)
    {
        let classNames:string[] = [];
        let needSetFocusOnSurface:boolean = false;

        if( this.props.allowAnimation )
        {
            if( prevProps.isActive && !this.props.isActive )
            {
                classNames.push('disappearingPage');
                classNames.push('wasChangedActivePage');
                needSetFocusOnSurface = true;
            }

            if( !prevProps.isActive && this.props.isActive )
            {
                classNames.push('appearingPage');
                classNames.push('wasChangedActivePage');
            }

            if( !prevProps.isOverlay && this.props.isOverlay )
                classNames.push('appearingOverlay');

            if( prevProps.isOverlay && !this.props.isOverlay )
                classNames.push('disappearingOverlay');

            if( classNames.length > 0 )
            {
                this.setState({animationClassNames: classNames}, needSetFocusOnSurface && this._focusSurfaceArea);
            }
        }
        else
        {
            if( prevProps.isActive && !this.props.isActive )
            {
                this._focusSurfaceArea();
                needSetFocusOnSurface = true;
            }

            if( this.state.animationClassNames.length > 0 )
                this.setState({animationClassNames: []}, needSetFocusOnSurface && this._focusSurfaceArea);
        }
    }

    public render():JSX.Element
    {
        if( this.props.isReactive )
            return this._render();

        return untracked(() => this._render());
    }

    private _render():JSX.Element
    {
        const {surfaceArea, surfaceBoxReparenting} = controllers.ui;
        const {page, stackIndex, isActive, isOverlay, isHidden, isReactive} = this.props;
        const {animationClassNames} = this.state;

        const isInZenPreviewMode:boolean = surfaceArea.isInZenPreviewMode();

        if( !isActive && !isOverlay )
            return null;

        let viewportWidth;
        let viewportHeight;

        const boxesElements:Array<JSX.Element> = page.artboard.visualBoxes.reduce((boxesElements, childVisualBox:VisualBox) =>
        {
            if( childVisualBox.box.isViewport )
            {
                const {layoutWidth, layoutHeight} = childVisualBox.box.componentDesc;

                if( layoutWidth && layoutHeight )
                {
                    viewportWidth = layoutWidth.value.value;
                    viewportHeight = layoutHeight.value.value;
                }
            }

            /**
             * todo:
             *
             * This is a temporary fix.
             * When pressing CMD + {repeated component}, hide the “funny thing” at 0,0 for now (some instance of the component)
             *
             * Remove this code after fixing https://minter.atlassian.net/browse/MAM-3762
             */
            if( surfaceBoxReparenting.isReparentingBox(childVisualBox.box) )
                return [
                    ...boxesElements,
                    <BoxView
                        isReactive={isReactive}
                        key={`${childVisualBox.box.id}`}
                        visualBox={childVisualBox}
                        repeater={Repeater.initial()}
                        isInOverlays={isOverlay}
                        isHiddenOverlay={isHidden}
                    />
                ];

            return [
                ...boxesElements,
                ...childVisualBox.repeaters.map((repeater:Repeater) => (
                    <BoxView
                        isReactive={isReactive}
                        key={`${childVisualBox.box.id}_${repeater.index}`}
                        visualBox={childVisualBox}
                        repeater={repeater}
                        isInOverlays={isOverlay}
                        isHiddenOverlay={isHidden}
                    />
                ))
            ];
        }, []);

        const style = {
            width: isInZenPreviewMode ? '100%' : viewportWidth,
            height: isInZenPreviewMode ? '100%' : viewportHeight,
            zIndex: isActive
                ? 1
                : isOverlay
                    ? 2 + stackIndex
                    : 0
        };

        const childrenStyle = css({
            [`&:not(.${styles.isActive}):not(.${styles.isShowedOverlay}) [data-box-view="true"]`]: {
                pointerEvents: 'none !important'
            }
        });

        const animationClassNamesObj:any = animationClassNames.reduce((res:any, className:string) =>
        {
            res[className] = true;
            return res;
        }, {});

        return (
            <div
                data-page-id={page.id}
                className={cx('page', {
                    isOverlay,
                    isActive,
                    isShowedOverlay: isOverlay && !isHidden,
                    ...animationClassNamesObj
                })}
                style={style}
                {...childrenStyle}
            >
                {boxesElements}
                {this._renderBoxesInReparentingByOtherUsers()}
            </div>
        );
    }

    private _renderBoxesInReparentingByOtherUsers()
    {
        const {page, isOverlay, isHidden, isReactive} = this.props;
        const {activeUsers} = rootState.ui.visualDesigner;

        const boxes:JSX.Element[] = [];

        activeUsers.otherUserBoxReparentingState.forEach((moveState, boxId) =>
        {
            const box = page.artboard.allBoxes.get(boxId);
            
            if( !box )
                return;

            const visualBox = new VisualBox(box, null, Repeater.initial());

            boxes.push(<BoxView
                isReactive={isReactive}
                key={`reparentingState_${box.id}`}
                visualBox={visualBox}
                repeater={Repeater.initial()}
                isInOverlays={isOverlay}
                isHiddenOverlay={isHidden}
            />);
        });

        return boxes;
    }
}
